import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { useAuth } from '@/auth/useAuth';
import { apiError } from '@/lib/apiError';
import { ledgerOptions, listTdsSections } from '@/api/accountsBooks';
import {
  alterTdsCert, alterTdsLdc, alterTdsReturn, createTdsCert, createTdsLdc, createTdsReturn, deleteTdsCert, deleteTdsLdc, deleteTdsReturn,
  getTdsChallans, getTdsDeductions, getTdsDueDates, getTdsForm16a, getTdsLdc, getTdsOutstanding, getTdsParties, getTdsPayable,
  getTdsReconciliation, getTdsReturn, getTdsSections, getTdsThresholds, type Form16aRow, type LdcRow,
} from '@/api/accountsTds';
import { VoucherFormModal } from '../books/VoucherFormModal';
import { amt, tdate, today } from '../reports/reportUtils';
import { TdsRegister, type Col } from './TdsRegister';
import { useFy } from './tdsUtils';
import { useVoucherOpen } from './useVoucherOpen';

/* The TDS screens of the prototype's TDS Reports group — each a register over the books' TDS. */

const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';
const key = (k: string, fy?: string) => ['acc-books', 'tds', k, fy];


type D = Awaited<ReturnType<typeof getTdsDeductions>>['rows'][number];

function DeductionsRegister({ title, cols }: { title: string; cols: Col<D & { secL: string }>[] }) {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('deductions', fy), queryFn: () => getTdsDeductions({ fy }) });
  const v = useVoucherOpen(true);
  return (
    <>
      <TdsRegister title={title} cols={cols} rows={(q.data?.rows ?? []).map((r) => ({ ...r, secL: r.section }))} q={q} open={(r) => v.open(r.voucherId)} rowKey={(r) => `${r.voucherId}-${r.section}`} />
      {v.el}
    </>
  );
}

export const TdsDeductionsPage = () => (
  <DeductionsRegister title="TDS Deduction Entry" cols={[['date', 'Date', 'd'], ['voucherNo', 'Voucher No.', 'mono'], ['party', 'Party', 't'], ['pan', 'PAN', 'mono'], ['section', 'Section', 'mono'], ['gross', 'Bill Amount (excl. GST)', 'm'], ['base', 'TDS Base', 'm'], ['rate', 'TDS Rate', 'p'], ['tds', 'TDS Amount', 'm']]} />
);
export const TdsRatesPage = () => (
  <DeductionsRegister title="TDS Rate Auto Calculation" cols={[['date', 'Date', 'd'], ['party', 'Party', 't'], ['pan', 'PAN', 'mono'], ['deductee', 'Deductee', 't'], ['section', 'Section', 'mono'], ['gross', 'This Bill', 'm'], ['cum', 'YTD Payments', 'm0'], ['base', 'Base Amount', 'm'], ['rate', 'Rate %', 'p'], ['tds', 'TDS Amount', 'm'], ['reason', 'Threshold / Rate Check', 'w']]} />
);
export const TdsDeductedPage = () => (
  <DeductionsRegister title="TDS Deducted Report" cols={[['date', 'Date', 'd'], ['voucherNo', 'Voucher No.', 'mono'], ['party', 'Party', 't'], ['section', 'Section', 'mono'], ['gross', 'Gross Amount', 'm'], ['tds', 'TDS Amount', 'm'], ['net', 'Net Payable', 'm']]} />
);

export function TdsSectionReportPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('sections', fy), queryFn: () => getTdsSections({ fy }) });
  return (
    <TdsRegister title="TDS Section-wise Report" q={q} rows={q.data?.rows ?? []} rowKey={(r) => r.section}
      note="A negative balance is an excess challan, to adjust against a later month."
      cols={[['section', 'Section', 'mono'], ['oldSection', 'Old Act', 'mono'], ['nature', 'Nature of Payment', 't'], ['parties', 'Parties', 'n'], ['base', 'Payment Amount', 'm'], ['tds', 'TDS Amount', 'm'], ['paid', 'Challan Paid', 'm'], ['balance', 'Balance', 'm']]} />
  );
}

export function TdsPartyPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('parties', fy), queryFn: () => getTdsParties({ fy }) });
  return (
    <TdsRegister title="Party-wise TDS Ledger" q={q} rows={q.data?.rows ?? []} rowKey={(r) => `${r.party}-${r.section}`}
      cols={[['party', 'Party', 't'], ['section', 'Section', 'mono'], ['opening', 'Opening', 'm'], ['tds', 'TDS Deducted', 'm'], ['deposited', 'TDS Deposited', 'm'], ['closing', 'Closing Balance', 'm']]} />
  );
}

export function TdsPanPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('parties', fy), queryFn: () => getTdsParties({ fy }) });
  return (
    <TdsRegister title="PAN-wise TDS Report" q={q} rows={(q.data?.rows ?? []).map((r) => ({ ...r, pan: r.pan ?? 'No PAN', diff: r.closing }))} rowKey={(r) => `${r.party}-${r.section}`}
      cols={[['pan', 'PAN', 'mono'], ['party', 'Party Name', 't'], ['section', 'Section', 'mono'], ['base', 'Payment Amount', 'm'], ['tds', 'TDS Deducted', 'm'], ['deposited', 'TDS Deposited', 'm'], ['diff', 'Difference', 'm']]} />
  );
}

export function TdsPayablePage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('payable', fy), queryFn: () => getTdsPayable({ fy }) });
  return (
    <TdsRegister title="TDS Payable Report" q={q} rows={q.data?.rows ?? []} rowKey={(r) => `${r.month}-${r.section}`}
      cols={[['asOn', 'As on Date', 'd'], ['month', 'Month', 'mo'], ['section', 'Section', 'mono'], ['opening', 'Opening Payable', 'm0'], ['current', 'Current TDS', 'm'], ['paid', 'Paid', 'm'], ['closing', 'Closing Payable', 'm0']]} />
  );
}

export function TdsChallansPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('challans', fy), queryFn: () => getTdsChallans({ fy }) });
  const v = useVoucherOpen(true);
  const canPay = useAuth().hasPerm('acc-payment');
  const [creating, setCreating] = useState(false);
  return (
    <>
      <TdsRegister title="TDS Challan Entry" q={q} rows={q.data?.rows ?? []} open={(r) => v.open(r.voucherId)} rowKey={(r) => String(r.voucherId)}
        toolbar={canPay ? <div className="col-span-3 sm:col-auto sm:self-end"><Button variant="primary" size="sm" onClick={() => setCreating(true)}><Plus size={14} /> New challan</Button></div> : undefined}
        cols={[['date', 'Challan Date', 'd'], ['bsr', 'BSR Code', 'mono'], ['serial', 'Challan Serial No.', 'mono'], ['fy', 'FY', 't'], ['section', 'Section', 'mono'], ['month', 'For Month', 'mo'], ['tax', 'Tax', 'm'], ['interest', 'Interest', 'm'], ['fee', 'Late Fee', 'm'], ['total', 'Amount Paid', 'm'], ['bank', 'Bank', 't'], ['status', 'Status', 's']]} />
      {v.el}
      {creating && <VoucherFormModal type="Payment" voucher={null} startChallan onClose={() => setCreating(false)} />}
    </>
  );
}

export function TdsAdjustPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('challans', fy), queryFn: () => getTdsChallans({ fy }) });
  const v = useVoucherOpen(true);
  return (
    <>
      <TdsRegister title="Challan-wise Adjustment" q={q} rows={(q.data?.rows ?? []).map((r) => ({ ...r, extra: r.interest + r.fee }))} open={(r) => v.open(r.voucherId)} rowKey={(r) => String(r.voucherId)}
        cols={[['serial', 'Challan No.', 'mono'], ['date', 'Date', 'd'], ['bsr', 'BSR', 'mono'], ['section', 'Section', 'mono'], ['deductees', 'Deductees', 'n'], ['tax', 'Challan Amount', 'm'], ['allocated', 'Amount Used', 'm'], ['extra', 'Interest / Fee', 'm'], ['unadjusted', 'Balance (Unadjusted)', 'm']]} />
      {v.el}
    </>
  );
}

export function TdsReturnPage() {
  const [fy] = useFy();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const q = useQuery({ queryKey: key('return', fy), queryFn: () => getTdsReturn({ fy }) });
  const v = useVoucherOpen(true);
  const [draft, setDraft] = useState<{ id: number | null; quarter: string; filedOn: string; token: string; note: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['acc-books', 'tds'] });
  const save = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => { const b = { fy, quarter: d.quarter, filedOn: d.filedOn, token: d.token.trim(), note: d.note.trim() || null }; return d.id ? alterTdsReturn(d.id, b) : createTdsReturn(b); },
    onSuccess: () => { toast.push('Return saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const del = useMutation({ mutationFn: deleteTdsReturn, onSuccess: () => { toast.push('Return entry deleted', 'success'); refresh(); }, onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error') });
  return (
    <>
      {q.data && (
        <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4 print:hidden">
          {q.data.quarters.map((x) => (
            <div key={x.quarter} className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5 text-xs">
              <div className="flex items-center justify-between"><b>{x.quarter} · 26Q</b><span className="text-[var(--text-muted)]">due {tdate(x.due)}</span></div>
              {x.filed ? (
                <div className="mt-1.5">
                  <div className="font-semibold text-success">Filed {tdate(x.filed.filedOn)}</div>
                  <div className="font-mono text-[11px] text-[var(--text-muted)]">{x.filed.token}</div>
                  <div className="mt-1 flex gap-1">
                    <Button variant="glass" size="sm" onClick={() => setDraft({ id: x.filed!.id, quarter: x.quarter, filedOn: x.filed!.filedOn, token: x.filed!.token, note: x.filed!.note ?? '' })}>Alter</Button>
                    <button type="button" className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete" onClick={async () => { if (await confirmDlg(`Delete the ${x.quarter} return entry?`)) del.mutate(x.filed!.id); }}><Trash2 size={14} /></button>
                  </div>
                </div>
              ) : (
                <div className="mt-1.5"><Button variant="glass" size="sm" onClick={() => setDraft({ id: null, quarter: x.quarter, filedOn: today(), token: '', note: '' })}>Mark filed</Button></div>
              )}
            </div>
          ))}
        </div>
      )}
      <TdsRegister title="TDS Return Data" q={q} rows={q.data?.rows ?? []} open={(r) => v.open(r.voucherId)} rowKey={(r) => `${r.voucherId}-${r.section}`}
        cols={[['quarter', 'Quarter', 't'], ['form', 'Form', 't'], ['pan', 'PAN', 'mono'], ['party', 'Deductee Name', 't'], ['section', 'Section', 'mono'], ['date', 'Date of Credit', 'd'], ['base', 'Amount Paid / Credited', 'm'], ['tds', 'TDS', 'm'], ['status', 'Status', 's']]} />
      {v.el}
      <Modal open={!!draft} onClose={() => setDraft(null)} title={draft ? `${draft.id ? 'Alter' : 'Mark filed'} — 26Q ${draft.quarter} ${fy}` : ''} size="sm"
        footer={<><Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button><Button variant="primary" disabled={save.isPending} onClick={() => draft && save.mutate(draft)}>{save.isPending ? 'Saving…' : 'Save'}</Button></>}>
        {draft && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><label className={label}>Filed on</label><Input type="date" max={today()} value={draft.filedOn} onChange={(e) => setDraft({ ...draft, filedOn: e.target.value })} /></div>
              <div><label className={label}>Token / acknowledgement no.</label><Input className="font-mono" maxLength={30} value={draft.token} onChange={(e) => setDraft({ ...draft, token: e.target.value })} /></div>
            </div>
            <div><label className={label}>Note</label><Input maxLength={200} value={draft.note} placeholder="e.g. filed by CA" onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></div>
          </div>
        )}
      </Modal>
    </>
  );
}

export function TdsForm16aPage() {
  const [fy] = useFy();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const q = useQuery({ queryKey: key('form16a', fy), queryFn: () => getTdsForm16a({ fy }) });
  const [draft, setDraft] = useState<{ row: Form16aRow; certNo: string; issuedOn: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['acc-books', 'tds'] });
  const save = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => { const b = { fy, quarter: d.row.quarter, ledgerId: d.row.partyLedgerId!, section: d.row.section, certNo: d.certNo.trim(), issuedOn: d.issuedOn }; return d.row.certId ? alterTdsCert(d.row.certId, b) : createTdsCert(b); },
    onSuccess: () => { toast.push('Certificate saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const del = useMutation({ mutationFn: deleteTdsCert, onSuccess: () => { toast.push('Certificate deleted', 'success'); refresh(); }, onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error') });
  return (
    <>
      <TdsRegister title="Form 16A Details" q={q} rows={q.data?.rows ?? []} rowKey={(r) => `${r.quarter}-${r.partyLedgerId}-${r.section}`}
        note="Enter each certificate number from TRACES once it is downloaded."
        actions={(r) => (
          <span className="inline-flex gap-1">
            <Button variant="glass" size="sm" onClick={() => setDraft({ row: r, certNo: r.certNo ?? '', issuedOn: r.issuedOn ?? today() })}>{r.certId ? 'Alter' : 'Enter'}</Button>
            {r.certId && <button type="button" className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete" onClick={async () => { if (await confirmDlg(`Delete certificate ${r.certNo}?`)) del.mutate(r.certId!); }}><Trash2 size={14} /></button>}
          </span>
        )}
        cols={[['quarter', 'Quarter', 't'], ['party', 'Deductee', 't'], ['pan', 'PAN', 'mono'], ['section', 'Section', 'mono'], ['certNo', 'Certificate No.', 'mono'], ['tds', 'TDS Amount', 'm'], ['issuedOn', 'Issue Date', 'd'], ['status', 'Status', 's']]} />
      <Modal open={!!draft} onClose={() => setDraft(null)} title={draft ? `Form 16A — ${draft.row.party} · ${draft.row.section} · ${draft.row.quarter}` : ''} size="sm"
        footer={<><Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button><Button variant="primary" disabled={save.isPending} onClick={() => draft && save.mutate(draft)}>{save.isPending ? 'Saving…' : 'Save'}</Button></>}>
        {draft && (
          <div className="grid grid-cols-2 gap-2">
            <div><label className={label}>Certificate no.</label><Input className="font-mono" maxLength={30} value={draft.certNo} onChange={(e) => setDraft({ ...draft, certNo: e.target.value })} /></div>
            <div><label className={label}>Issued on</label><Input type="date" max={today()} value={draft.issuedOn} onChange={(e) => setDraft({ ...draft, issuedOn: e.target.value })} /></div>
            <p className="col-span-2 text-[11px] text-[var(--text-muted)]">TDS for the quarter: ₹{amt(draft.row.tds) || '0.00'}</p>
          </div>
        )}
      </Modal>
    </>
  );
}

export function TdsReconciliationPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('rec', fy), queryFn: () => getTdsReconciliation({ fy }) });
  return (
    <TdsRegister title="TDS Reconciliation" q={q} rows={q.data?.rows ?? []} rowKey={(r) => `${r.party}-${r.section}-${r.quarter}`}
      note="Return amount counts once the quarter's 26Q is marked filed. Portal amounts come with a TRACES upload (later)."
      cols={[['party', 'Party', 't'], ['section', 'Section', 'mono'], ['quarter', 'Quarter', 't'], ['books', 'Books TDS', 'm'], ['challan', 'Challan Paid', 'm'], ['returnAmount', 'Return Amount', 'm'], ['portal', 'Portal Amount', 'm0'], ['diff', 'Difference', 'm'], ['status', 'Status', 's']]} />
  );
}

export function TdsOutstandingPage() {
  const q = useQuery({ queryKey: key('outstanding'), queryFn: getTdsOutstanding });
  const v = useVoucherOpen(true);
  return (
    <>
      <TdsRegister title="TDS Outstanding Report" q={q} rows={q.data?.rows ?? []} asOn={q.data?.asOn} fyPicker={false} open={(r) => v.open(r.voucherId)} rowKey={(r) => `${r.voucherId}-${r.section}`}
        note="Interest u/s 201(1A): 1.5% a month or part of a month, from the month of deduction to this month, once past the due date."
        cols={[['party', 'Party', 't'], ['pan', 'PAN', 'mono'], ['section', 'Section', 'mono'], ['date', 'Deducted On', 'd'], ['pending', 'Due TDS', 'm'], ['due', 'Due Date', 'd'], ['overdueDays', 'Days Overdue', 'm0'], ['interest', 'Interest @1.5% pm', 'm'], ['status', 'Status', 's']]} />
      {v.el}
    </>
  );
}

export function TdsDuePage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('due', fy), queryFn: () => getTdsDueDates({ fy }) });
  return (
    <TdsRegister title="TDS Due Date Reminder" q={q} rows={q.data?.rows ?? []} rowKey={(r) => `${r.compliance}-${r.period}`}
      note="Deposit by the 7th of the next month (30 April for March); 26Q by the month after the quarter (31 May for Q4); Form 16A 15 days after the return."
      cols={[['compliance', 'Compliance', 't'], ['period', 'Period', 'mo'], ['due', 'Due Date', 'd'], ['responsible', 'Responsible', 't'], ['amount', 'Expected Amount', 'm0'], ['status', 'Reminder Status', 's']]} />
  );
}

export function TdsThresholdPage() {
  const [fy] = useFy();
  const q = useQuery({ queryKey: key('threshold', fy), queryFn: () => getTdsThresholds({ fy }) });
  return (
    <TdsRegister title="TDS Threshold Limit Tracking" q={q} rows={q.data?.rows ?? []} rowKey={(r) => `${r.partyLedgerId}-${r.section}`}
      cols={[['party', 'Party', 't'], ['pan', 'PAN', 'mono'], ['section', 'Section', 'mono'], ['ytd', 'YTD Payments', 'm'], ['limit', 'Threshold Limit', 't'], ['headroom', 'Headroom', 'm0'], ['applicable', 'TDS Applicable', 's']]} />
  );
}

type LdcDraft = { id: number | null; ledgerId: number | ''; section: string; certNo: string; rate: string; limit: string; validFrom: string; validTo: string };
export function TdsLdcPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const q = useQuery({ queryKey: key('ldc'), queryFn: getTdsLdc });
  const optsQ = useQuery({ queryKey: ['acc-books', 'ledger-options'], queryFn: ledgerOptions });
  const secQ = useQuery({ queryKey: ['acc-books', 'tds-sections'], queryFn: listTdsSections });
  const [draft, setDraft] = useState<LdcDraft | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['acc-books'] });
  const save = useMutation({
    mutationFn: (d: LdcDraft) => { const b = { ledgerId: d.ledgerId as number, section: d.section, certNo: d.certNo.trim(), rate: Number(d.rate), limit: Number(d.limit), validFrom: d.validFrom, validTo: d.validTo }; return d.id ? alterTdsLdc(d.id, b) : createTdsLdc(b); },
    onSuccess: () => { toast.push('Certificate saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const del = useMutation({ mutationFn: deleteTdsLdc, onSuccess: () => { toast.push('Certificate deleted', 'success'); refresh(); }, onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error') });
  const fyStart = `${today().slice(0, 4)}-04-01`;
  const open = (r?: LdcRow) => setDraft(r
    ? { id: r.id, ledgerId: r.ledgerId, section: r.section, certNo: r.certNo, rate: String(r.rate), limit: String(r.limit), validFrom: r.validFrom, validTo: r.validTo }
    : { id: null, ledgerId: '', section: '', certNo: '', rate: '', limit: '', validFrom: fyStart, validTo: `${Number(fyStart.slice(0, 4)) + 1}-03-31` });
  const suppliers = (optsQ.data ?? []).filter((l) => l.role === 'C');
  return (
    <>
      <TdsRegister title="Lower / Nil TDS Certificate Details" q={q} rows={q.data ?? []} fyPicker={false} asOn={today()} rowKey={(r) => String(r.id)}
        note="A certificate's rate applies on the deductee's bills of its section within its dates, up to its limit; then the normal rate."
        toolbar={<div className="col-span-3 sm:col-auto sm:self-end"><Button variant="primary" size="sm" onClick={() => open()}><Plus size={14} /> New certificate</Button></div>}
        actions={(r) => (
          <span className="inline-flex gap-1">
            <Button variant="glass" size="sm" onClick={() => open(r)}>Alter</Button>
            {!r.used && <button type="button" className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete" onClick={async () => { if (await confirmDlg(`Delete certificate ${r.certNo}?`)) del.mutate(r.id); }}><Trash2 size={14} /></button>}
          </span>
        )}
        cols={[['party', 'Party', 't'], ['pan', 'PAN', 'mono'], ['section', 'Section', 'mono'], ['certNo', 'Certificate No.', 'mono'], ['validFrom', 'Valid From', 'd'], ['validTo', 'Valid To', 'd'], ['rate', 'Rate %', 'p'], ['limit', 'Limit', 'm0'], ['used', 'Utilised', 'm0'], ['balance', 'Balance', 'm0'], ['status', 'Status', 's']]} />
      <Modal open={!!draft} onClose={() => setDraft(null)} title={draft?.id ? 'Alter certificate' : 'New lower / nil deduction certificate'} size="sm"
        footer={<><Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button><Button variant="primary" disabled={save.isPending} onClick={() => draft && save.mutate(draft)}>{save.isPending ? 'Saving…' : 'Save'}</Button></>}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className={label}>Deductee</label>
              <SearchableSelect options={suppliers.map((s) => ({ value: s.id, label: s.name, sublabel: s.pan ?? 'No PAN' }))} value={draft.ledgerId === '' ? 0 : draft.ledgerId} placeholder="Supplier" onChange={(v) => setDraft({ ...draft, ledgerId: v ? Number(v) : '' })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={label}>Section</label><Select value={draft.section} onChange={(e) => setDraft({ ...draft, section: e.target.value })}><option value="">Choose…</option>{(secQ.data ?? []).map((x) => <option key={x.code} value={x.code}>{x.code} · {x.nature}</option>)}</Select></div>
              <div><label className={label}>Certificate no.</label><Input className="font-mono" maxLength={30} value={draft.certNo} onChange={(e) => setDraft({ ...draft, certNo: e.target.value })} /></div>
              <div><label className={label}>Rate %</label><Input type="number" min={0} max={50} step="0.01" value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} /></div>
              <div><label className={label}>Limit (₹)</label><Input type="number" min={0} step="0.01" value={draft.limit} onChange={(e) => setDraft({ ...draft, limit: e.target.value })} /></div>
              <div><label className={label}>Valid from</label><Input type="date" value={draft.validFrom} onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })} /></div>
              <div><label className={label}>Valid to</label><Input type="date" value={draft.validTo} onChange={(e) => setDraft({ ...draft, validTo: e.target.value })} /></div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
